import { events } from '../../data/events';
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import './Events.css';
import { fetchEventStatus } from '../../services/rsvpService';
import { loadEventStatusesFromStorage, saveEventStatusesToStorage, upsertEventStatusInStorage } from '../../utils/eventStatusUtils';

// Events data now lives in src/data/events.js (re-exported here so existing
// imports from '../events/Events' keep working). See docs/EDITING.md.
export { events };


function Events() {
  const [searchParams] = useSearchParams();
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [statusBySlug, setStatusBySlug] = useState(() => {
    return loadEventStatusesFromStorage();
  });

  // Split events into future and past
  const today = new Date();
  const futureEvents = events.filter(event => getEventSortDate(event) >= today);
  const pastEvents = events.filter(event => getEventSortDate(event) < today)
    .sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort most recent first

  // derive stable list of slugs for all events (used only to recompute open list inside effect)
  const allEventSlugs = useMemo(() => events.map(e => e.rsvpLink.replace('/rsvp/', '')), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Only fetch open (future and not marked closed) events
        const now = new Date();
        const openEvents = events.filter(e => getEventSortDate(e) >= now && !e.isClosed);
        const slugsToFetch = openEvents.map(e => e.rsvpLink.replace('/rsvp/', ''));

        const results = await Promise.all(
          slugsToFetch.map(async (slug) => {
            const s = await fetchEventStatus(slug).catch(() => null);
            return [slug, s && typeof s === 'object' ? s : null];
          })
        );
        if (!cancelled) {
          const map = {};
          const existing = loadEventStatusesFromStorage();
          for (const [slug, s] of results) {
            if (s && s.slug && (s.capacity == null || Number.isFinite(Number(s.capacity))) ) {
              map[slug] = s;
              upsertEventStatusInStorage(slug, s);
            }
          }
          // Merge with existing cache; avoid wiping cache if nothing fetched
          const merged = { ...existing, ...map };
          if (Object.keys(map).length > 0) {
            saveEventStatusesToStorage(merged);
          }
          setStatusBySlug(merged);
        }
      } catch (err) {
        try { console.error('[Events] status fetch error:', err); } catch (_) {}
      }
    })();
    return () => { cancelled = true; };
  }, [allEventSlugs]);

  const getSlug = (event) => event.rsvpLink.replace('/rsvp/', '');
  const isEventFull = (event) => {
    const slug = getSlug(event);
    const s = statusBySlug[slug];
    return !!(s && s.capacity != null && s.remaining <= 0);
  };

  useEffect(() => {
    const expandParam = searchParams.get('expand');
    if (expandParam) {
      setExpandedEventId(Number(expandParam));
      setTimeout(() => {
        const element = document.getElementById('upcoming-programs');
        if (element) {
          const headerOffset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [searchParams]);

  const toggleExpand = (eventId) => {
    setExpandedEventId(expandedEventId === eventId ? null : eventId);
  };

  const handleImageClick = (src) => {
    setModalImage(src);
  };

  const handleCloseModal = () => {
    setModalImage(null);
  };

  const isPast = (event) => event.isClosed || getEventSortDate(event) < new Date();

  const renderEventCard = (event) => {
    const past = isPast(event);
    const full = isEventFull(event);
    const disabled = past || full;

    return (
      <div
        key={event.id}
        id={`event-${event.id}`}
        className={`card event-card feature-card ${expandedEventId === event.id ? 'expanded' : ''}`}
      >
        <h3 className="event-title">{event.title}</h3>

        <div className="event-subheader">
          <div className="event-info-card">
            <div className="date-time-line">
              <span className="date">{formatEventDisplayDate(event)}</span>
              <div className="time-schedule">
                <span className="time-item">Social - {event.times.social}</span>
                <span className="dot-separator">•</span>
                <span className="time-item">Dinner - {event.times.dinner}</span>
                <span className="dot-separator">•</span>
                <span className="time-item">Program - {event.times.program}</span>
              </div>
            </div>
            <div className="speaker-line">
              <strong>Presenter:</strong> <span className="speaker-name">{event.presenter}</span>
            </div>
            {event.location && (
              <div className="event-location-line">
                <strong>Location:</strong>{' '}
                <span className="event-location">{event.location}</span>
              </div>
            )}
          </div>
        </div>

        <div className="content-with-image">
          {event.image && (
            <img
              src={event.image}
              alt={event.title}
              className="event-thumbnail"
              onClick={() => handleImageClick(event.image)}
            />
          )}
          <div className="program-preview">
            <h4 className="section-heading">Program</h4>
            {expandedEventId === event.id
              ? event.program
              : <>
                  {event.program.substring(0, 600)}...
                  <button className="button button--sm read-more-button" onClick={() => toggleExpand(event.id)}>Read More →</button>
                </>
            }
          </div>
        </div>

        {expandedEventId === event.id && (
          <>
            <div className="presenter-bio">
              <h4>About the Presenter</h4>
              <p>{event.presenterBio}</p>
            </div>

            {event.additionalImages && event.additionalImages.length > 0 && (
              <div className="additional-images">
                <h4>Additional Images</h4>
                <div className="images-grid">
                  {event.additionalImages.map((img, idx) => (
                    <div key={idx} className="additional-image-container" onClick={() => handleImageClick(img.src)}>
                      <img src={img.src} alt={img.alt} className="additional-image" />
                      <div className="enlarge-text">Click to enlarge</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button className="button show-less-button" onClick={() => toggleExpand(event.id)}>Show Less</button>
          </>
        )}

        <div className="rsvp-info">
          <div className="rsvp-content">
            <div className="rsvp-text">
              <p className="deadline">{event.rsvpDeadline}</p>
              {event.price && <p className="price">{event.price}</p>}
              {event.paymentNote && <p className="payment-note">{event.paymentNote}</p>}
            </div>
            <a
              href={event.rsvpLink}
              className={`button rsvp-button ${disabled ? 'disabled' : ''}`}
              aria-disabled={disabled}
              onClick={(e) => { if (disabled) e.preventDefault(); }}
            >
              {past ? 'RSVP Deadline Passed' : (full ? 'Event Full' : 'RSVP Now')}
            </a>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="events">
      {modalImage && (
        <div className="image-modal-overlay" onClick={handleCloseModal}>
          <div className="image-modal-content">
            <button className="image-modal-close" onClick={handleCloseModal}>×</button>
            <img src={modalImage} alt="Full size" />
          </div>
        </div>
      )}
      <section className="page-hero">
        <h1>Events</h1>
        <p>Historical Society Lectures & Programs</p>
      </section>

      <div className="page-container">
        {futureEvents.length > 0 && (
          <section className="upcoming-events">
            <h2 id="upcoming-programs">Upcoming Programs</h2>
            <div className="events-grid">
              {futureEvents.map((event) => renderEventCard(event))}
            </div>
          </section>
        )}

        {futureEvents.length > 0 && pastEvents.length > 0 && (
          <hr className="section-divider" />
        )}

        {pastEvents.length > 0 && (
          <section className="past-events">
            <h2>Past Programs</h2>
            <div className="events-grid">
              {pastEvents.map((event) => renderEventCard(event))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export const formatEventDate = (dateString) => {
  const date = new Date(dateString);
  const options = { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  };
  return date.toLocaleDateString('en-US', options)
};

/** Used for future/past filters and sorting when `dateTbd` is true (keeps event in “upcoming” until a real date is set). */
export function getEventSortDate(event) {
  if (!event) return new Date(0);
  if (event.dateTbd) return new Date('2099-12-31T12:00:00');
  const d = new Date(event.date);
  return Number.isNaN(d.getTime()) ? new Date(0) : d;
}

/** Prefer this for UI when an event may have `dateTbd`. */
export function formatEventDisplayDate(event) {
  if (event?.dateTbd) return 'Date TBD';
  return formatEventDate(event.date);
}

export default Events; 